import { ODataHttpClient } from "@odata2ts/http-client-api";
import { ODataModelPayloadV4 } from "@odata2ts/odata-core";
import { CollectionQueryBuilderV4, createQueryBuilderV4, ModelQueryBuilderV4 } from "@odata2ts/odata-query-builder";
import { QueryObjectModel } from "@odata2ts/odata-query-objects";
import { ODataServiceOptionsInternal } from "../ODataServiceOptions";
import { ServiceStateHelper } from "../ServiceStateHelper.js";

export interface SubtypeOptions {
  withCastPathSegment?: boolean;
  withTypeControlInfo?: boolean;
}

export class ServiceStateHelperV4<
  in out ClientType extends ODataHttpClient,
  Q extends QueryObjectModel,
> extends ServiceStateHelper<ClientType> {
  public constructor(
    client: ClientType,
    basePath: string,
    name: string,
    public qModel: Q,
    options?: ODataServiceOptionsInternal,
  ) {
    super(client, basePath, name, options);
  }

  public createQueryBuilder = (
    queryFn?: (builder: CollectionQueryBuilderV4<Q>, qObject: Q) => void,
    path = this.path,
  ): CollectionQueryBuilderV4<Q> => {
    const builder = createQueryBuilderV4(path, this.qModel, { unencoded: this.isUrlNotEncoded() });
    if (queryFn) {
      queryFn(builder, this.qModel);
    }

    return builder;
  };

  public createModelQueryBuilder = (
    queryFn?: (builder: ModelQueryBuilderV4<Q>, qObject: Q) => void,
    path = this.path,
  ): ModelQueryBuilderV4<Q> => {
    const builder = createQueryBuilderV4(path, this.qModel, { unencoded: this.isUrlNotEncoded() });
    if (queryFn) {
      queryFn(builder, this.qModel);
    }

    return builder;
  };

  public evaluateSubtypeOptions(options: SubtypeOptions | undefined) {
    const isSubtype = !!this.options.subtype;
    const dontUseCastPathSegment = isSubtype && !options?.withCastPathSegment;
    return {
      dontUseCastPathSegment,
      useTypeCi:
        (isSubtype && dontUseCastPathSegment && options?.withTypeControlInfo !== false) ||
        (isSubtype && options?.withTypeControlInfo),
    };
  }

  public addTypeControlInfo<T>(model: ODataModelPayloadV4<T>) {
    return {
      "@odata.type": `#${this.name}`,
      ...model,
    };
  }
}
